import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowUpRight, Flag, CheckCircle2 as CheckCircle, Hourglass, Rocket as RocketLaunch } from "lucide-react";
import VitalityVaultBanner from "../components/VitalityVaultBanner";
import LaunchDayBanner from "../components/LaunchDayBanner";

const Stat = ({ label, value, accent }) => (
  <div className="border border-[#E2E4E9] bg-white p-6 hover:border-[#0A0A0B] transition-colors" data-testid={`stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
    <div className="text-eyebrow">{label}</div>
    <div className={`font-display font-black text-5xl mt-2 tracking-tighter ${accent || ""}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [brands, setBrands] = useState([]);
  const [subscription, setSubscription] = useState(null);

  const refreshSub = () => api.billingMe().then(setSubscription).catch(() => {});

  useEffect(() => {
    api.dashboardStats().then(setStats);
    api.listBrands().then(setBrands);
    refreshSub();
  }, []);

  return (
    <div data-testid="dashboard-page" className="max-w-7xl">
      <div className="mb-10">
        <div className="text-eyebrow mb-3">Control Room</div>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter">Overview</h1>
        <p className="text-[#52525B] mt-3">Everything in flight across all your brands.</p>
      </div>

      <VitalityVaultBanner subscription={subscription} onTrialStarted={refreshSub} />
      <LaunchDayBanner />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 mb-12">
        <Stat label="Brands" value={stats?.brands ?? "—"} />
        <Stat label="Pending" value={stats?.pending_approvals ?? "—"} accent="text-[#F59E0B]" />
        <Stat label="Approved" value={stats?.approved_queue ?? "—"} accent="text-[#002FA7]" />
        <Stat label="Published" value={stats?.published ?? "—"} accent="text-[#059669]" />
        <Stat label="Flags" value={stats?.open_flags ?? "—"} accent="text-[#E11D48]" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-[#E2E4E9] bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-eyebrow">Brands</div>
              <h2 className="font-display font-bold text-2xl tracking-tight">Active rosters</h2>
            </div>
            <Link to="/brands" data-testid="manage-brands-link" className="text-eyebrow flex items-center gap-1 hover:text-[#002FA7]">
              Manage <ArrowUpRight size={14} />
            </Link>
          </div>
          {brands.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-[#52525B] mb-4">No brands yet. Add one to start the AI interview.</p>
              <Link to="/brands" data-testid="add-first-brand-link" className="inline-block bg-[#0A0A0B] text-white px-5 py-3 text-sm">
                Add a brand
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-[#E2E4E9]">
              {brands.slice(0, 5).map((b) => (
                <Link
                  to={`/brands/${b.brand_id}`}
                  key={b.brand_id}
                  className="flex items-center justify-between py-3 hover:bg-[#F4F5F6] px-3 -mx-3 transition-colors"
                  data-testid={`brand-row-${b.brand_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10" style={{ background: b.color || "#002FA7" }} />
                    <div>
                      <div className="font-semibold">{b.name}</div>
                      <div className="text-xs text-[#52525B] mono">
                        {b.is_client ? "CLIENT" : "OWNED"} · knowledge {b.knowledge_score}/100
                      </div>
                    </div>
                  </div>
                  <div className="text-xs mono text-[#A1A1AA]">{b.posts_generated} posts</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="border border-[#E2E4E9] bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-eyebrow">Up next</div>
              <h2 className="font-display font-bold text-2xl tracking-tight">Approval queue</h2>
            </div>
            <Link to="/approvals" data-testid="review-queue-link" className="text-eyebrow flex items-center gap-1 hover:text-[#002FA7]">
              Review <ArrowUpRight size={14} />
            </Link>
          </div>
          {!stats?.upcoming?.length ? (
            <p className="text-sm text-[#52525B] py-6">No posts in queue yet.</p>
          ) : (
            <div className="space-y-3">
              {stats.upcoming.map((p) => (
                <div key={p.post_id} className="border border-[#E2E4E9] p-4 hover:border-[#0A0A0B]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs mono text-[#A1A1AA]">{(p.scheduled_date || "").slice(0, 10)}</span>
                    <span className={`text-xs mono uppercase ${p.status === "approved" ? "text-[#059669]" : "text-[#F59E0B]"}`}>{p.status}</span>
                  </div>
                  <div className="font-semibold">{p.title}</div>
                  <div className="mt-1 text-xs text-[#52525B] flex items-center gap-2">
                    <RocketLaunch size={12} /> Viral score {p.viral_score_prediction}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
